<script setup>
import { ref, watch } from 'vue';
import InputLabel from '@/components/general/InputLabel.vue';
import InputError from '@/components/general/InputError.vue';
import TextInput from '@/components/general/TextInput.vue';
import TextArea from '@/components/general/TextArea.vue';
import api from '@/services/axios';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: '',
    },
    label: {
        type: String,
        required: true,
    },
    options: {
        type: Array,
        required: true,
    },
    error: {
        type: String,
        default: '',
    },
    showDescription: {
        type: Boolean,
        default: true,
    },
    placeholder: {
        type: String,
        default: 'Select an option',
    },
    customPlaceholder: {
        type: String,
        default: 'Enter new option name',
    },
    descriptionPlaceholder: {
        type: String,
        default: 'Enter description (optional)',
    },
    route: {
        type: String,
        required: true,
    },
    isValid: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(['update:modelValue', 'update:options', 'update:isValid']);

const selectedValue = ref(props.modelValue);
const showCustomInput = ref(false);
const customName = ref('');
const customDescription = ref('');
const isLoading = ref(false);
const customError = ref('');

// Watch for changes in the modelValue prop
watch(() => props.modelValue, (newValue) => {
    selectedValue.value = newValue;
});

// Watch for changes in the options array
watch(() => props.options, (newOptions) => {
    // If the selected value is not in the new options, reset it
    if (selectedValue.value && !newOptions.some(option => option.id === selectedValue.value)) {
        selectedValue.value = '';
    }
});

watch(selectedValue, (newValue) => {
    if (newValue === 'other') {
        showCustomInput.value = true;
        customError.value = ''; // Clear error when showing custom input
        // emit('update:modelValue', ''); // Clear the selected value
        emit('update:isValid', false); // Mark as invalid when "Other" is selected
    } else {
        showCustomInput.value = false;
        emit('update:modelValue', newValue);
        emit('update:isValid', true); // Mark as valid when a real option is selected
    }
});

const handleCustomOption = async () => {
    if (customName.value?.trim()) {
        isLoading.value = true;
        customError.value = ''; // Clear previous errors

        try {
            const response = await api.put(props.route, {
                name: customName.value.trim(),
                description: customDescription.value?.trim() || ''
            });

            if (response.data) {
                // Update the options list with the new item
                const updatedOptions = [...props.options, response.data];
                emit('update:options', updatedOptions);

                // Update the selected value to the new option's ID
                selectedValue.value = response.data.id;
                emit('update:modelValue', response.data.id);
                emit('update:isValid', true); // Mark as valid after successful addition

                // Clear the form
                showCustomInput.value = false;
                customName.value = '';
                customDescription.value = '';
            }
        } catch (error) {
            console.error('Error adding option:', error);
            if (error.response?.data?.errors) {
                // Handle validation errors
                if (error.response.data.errors.name) {
                    // Customize error message based on the label
                    const errorMessage = error.response.data.errors.name[0];
                    if (errorMessage.includes('already been taken')) {
                        customError.value = `This ${props.label.toLowerCase()} already exists.`;
                    } else {
                        customError.value = errorMessage;
                    }
                } else if (error.response.data.message) {
                    customError.value = error.response.data.message;
                }
            } else {
                customError.value = `An error occurred while adding the ${props.label.toLowerCase()}.`;
            }
        } finally {
            isLoading.value = false;
        }
    }
};
</script>

<template>
    <div class="mb-4">
        <InputLabel :for="label.toLowerCase()" :value="`${label} (Optional)`" />
        <select
            :id="label.toLowerCase()"
            v-model="selectedValue"
            class="block p-4 mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
            :disabled="isLoading"
        >
            <option value="">{{ placeholder }}</option>
            <option v-for="option in options" :key="option.id" :value="option.id">
                {{ option.name }}
            </option>
            <option value="other">Other (Add New)</option>
        </select>
        <InputError :message="error" class="mt-2" />
    </div>

    <div v-if="showCustomInput" class="mb-4">
        <InputLabel :for="`custom-${label.toLowerCase()}`" :value="`New ${label} Name`" />
        <div class="flex flex-col gap-2">
            <TextInput
                :id="`custom-${label.toLowerCase()}`"
                v-model="customName"
                type="text"
                class="block p-4 mt-1 w-full"
                :placeholder="customPlaceholder"
                :disabled="isLoading"
                :class="{ 'border-red-500': customError }"
            />
            <InputError :message="customError" class="mt-2" />
            <div class="flex justify-end">
                <button
                    type="button"
                    @click="handleCustomOption"
                    class="px-4 py-2 mt-1 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="isLoading || !customName?.trim()"
                >
                    <span v-if="isLoading">Adding...</span>
                    <span v-else>Add</span>
                </button>
            </div>
        </div>
    </div>
</template>
